import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useEncodingDevice, useSnackbar } from 'contexts'
import { ROUTES } from 'routes'
import { SIDEBAR_MINIMIZED_WIDTH, SIDEBAR_WIDTH } from 'config'

import { Box, Divider, Link, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { BookOpenIcon, RadioReceiverIcon, ShoppingBagIcon, SmartphoneIcon } from 'lucide-react'

import { Alert, Button, MENU_PAPER_SHADOW, dimNeutralText } from '@repo/ui'

import {
  SIDEBAR_MENU_ANCHOR_ORIGIN,
  SIDEBAR_MENU_PAPER_SX,
  SIDEBAR_MENU_TRANSFORM_ORIGIN,
} from '../../sidebarMenuPlacement'
import { useChromeSettledPosition } from 'hooks/useChromeSettledPosition'

// The reader glyph — one place so the sidebar + the Create-certificates modal stay in
// parity. Swap this single import to change it everywhere the sidebar renders it.
const ReaderIcon = RadioReceiverIcon

// Fixed row height so the row is identical whether the sidebar is open or collapsed
// (matches the nav rows; keeps the divider above it from shifting on expand/collapse).
const ROW_HEIGHT = 48

interface EncodingDeviceSidebarButtonProps {
  sidebarOpen?: boolean
}

/**
 * Hardware-reader status next to the profile area (VERA-457/468). Uses the same glyph as
 * the Create-certificates modal, brand-purple when a reader is connected and greyed when
 * not. Always present. Clicking opens a popover styled like the profile menu — when no
 * reader is connected it explains what a hardware reader is and offers Learn more (→ the
 * setup guide) + Buy a reader (→ the shop).
 *
 * The reader is a PC/SC device bridged by the local Vera Reader Helper over a secure
 * localhost WebSocket; the helper auto-connects on Vera's origin (no manual pairing).
 */
export const EncodingDeviceSidebarButton = ({ sidebarOpen }: EncodingDeviceSidebarButtonProps) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { available, helperReachable, isConnected, readerName, permissionHintPending, helperEverSeen, requestLocalNetworkAccess } = useEncodingDevice()
  const { open: notify } = useSnackbar()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  // No connect toast: the sidebar row IS the persistent indicator — its reader glyph turns
  // brand-purple and the label reads "Reader connected", with a click-through popover for
  // details. A transient toast on top of a persistent status was redundant.

  if (!available) {
    return null
  }

  const open = Boolean(anchorEl)
  /* Reposition once the drawer's width transition settles — see `useChromeSettledPosition`. */
  const menuPosition = useChromeSettledPosition(open)
  // Brand-purple when connected (a brand touch in the main sidebar); greyed otherwise.
  const iconColor = isConnected ? theme.palette.brand[500] : theme.palette.text.disabled
  // 'Helper ready' told you the LOCAL APP's state, not the thing you actually care
  // about (is a physical reader plugged in) — reads as jargon out of context.
  // "Reader not connected" (not "No reader connected") — noun-first, parallel with
  // "Reader connected" instead of a garden-path "No reader ... connected?" parse, and
  // it doesn't wrongly imply a reader was ever plugged in the way "disconnected" would.
  const statusLabel = isConnected ? 'Reader connected' : helperReachable ? 'Reader not connected' : 'Reader'

  // No longer latched or dismissed-on-open. The hint retires when the helper
  // actually connects (see EncodingDeviceContext), so it reflects whether the
  // permission was granted rather than whether you happened to look at it.
  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)
  const goSetup = () => { handleClose(); navigate(ROUTES.READER_SETUP) }
  const goShop = () => { handleClose(); navigate(ROUTES.TAGS_SHOP) }
  // The Create-certificates modal is local state on CertificatesPage, so there is
  // no way to open it directly from here — ?create=1 is the handshake (same pattern
  // the Roles page uses for its Add-role modal).
  const goCreateWithPhone = () => { handleClose(); navigate(`${ROUTES.CERTIFICATES}?create=1`) }

  return (
    <>
      {/* Fixed ROW_HEIGHT so open/closed match exactly. pt:2 so the gap above (to the divider)
          matches the Settings row's gap below it. pl:3 + the label's marginLeft:3 (below) match
          SidebarList's nav rows exactly (Settings/Labs) — both are plain-icon rows with the same
          icon footprint, so they should share one text column. Profile is the one row that's
          SUPPOSED to sit further right, since its avatar is wider than any of these icons —
          matching it here would misalign against every icon row instead. */}
      <ListItem sx={{ pt: 2, pb: 1, pr: 3, pl: 3 }}>
        <ListItemButton
          disableRipple
          aria-label={isConnected ? 'Hardware reader connected' : 'Hardware reader status'}
          // Opts this row OUT of the nav-icon greeting animation (../../iconMotion.ts).
          //
          // That file called this row "the other borderline row… deliberately left
          // animated rather than silently reclassified" — which was the right call to
          // leave for a human, and the human has now made it. The reclassification is
          // not silent: this row is a CONTROL by that file's own test. It opens a
          // status popover about the local hardware bridge; it does not route anywhere,
          // so there is no destination for a greeting to express.
          //
          // The marker sits on the ROW rather than the icon, which is the mechanism
          // that file designed — a row opts out by carrying one attribute instead of
          // by someone remembering to add its glyph to a list.
          data-nav-control=""
          onClick={handleClick}
          sx={[
            { borderRadius: 2, minHeight: ROW_HEIGHT, height: ROW_HEIGHT, py: 0, pr: 4 },
            sidebarOpen
              ? { minWidth: SIDEBAR_WIDTH - 24, justifyContent: 'initial' }
              : { maxWidth: SIDEBAR_MINIMIZED_WIDTH - 24, justifyContent: 'center' },
          ]}
        >
          <ListItemIcon
            sx={{ color: iconColor, minWidth: 0, justifyContent: 'center', mr: sidebarOpen ? 0 : 'auto', position: 'relative' }}
          >
            <ReaderIcon strokeWidth={1} />
            {/* Unread heads-up marker. A dot rather than inline copy because the row
                has to stay one line at both sidebar widths — the explanation lives in
                the popover, which this dot is telling you to open. */}
            {permissionHintPending && !helperReachable && helperEverSeen && (
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.warning.main,
                  // Ring in the sidebar's own background so the dot reads as sitting
                  // ON the glyph rather than merging into its strokes.
                  border: `2px solid ${theme.palette.background.default}`,
                  boxSizing: 'content-box',
                }}
              />
            )}
          </ListItemIcon>
          {sidebarOpen && (
            <ListItemText
              disableTypography
              sx={{
                marginLeft: 3,
                fontSize: 16,
                fontFamily: 'Lato',
                /*
                  TWO states, matching the icon (VRG-671, Spencer 2026-10-06): until a reader
                  is connected the whole row reads as greyed out — label AND glyph on
                  `text.disabled` — and only a connected reader gets the normal row colour.

                  CONNECTED keeps the resting colour every other row in this rail uses.
                  `navRestText` — the `MuiListItemButton` override's resting value —
                  delegates to `dimNeutralText`, so this lands on the same colour by
                  construction. (It replaced `text.primary`, which made a passive status
                  line the joint-darkest text in the rail, tied with the SELECTED row.)
                  `dimNeutralText`, not `navRestText`, because `navRestText` takes MUI's
                  wide `Theme` and this repo resolves two `@mui/material` instances, so
                  passing `apps/platform`'s theme to it throws TS2345/TS2859 (navChrome.ts).

                  NOT CONNECTED (both 'Reader not connected' and the helper-unreachable
                  'Reader') uses `text.disabled`, the same token as the icon, so the two
                  dim together. This reverses the earlier one-token-for-both-states call,
                  which argued the label + icon already carried the state; the founder's
                  call is that the row should LOOK inactive until it is.

                  ⚠️ Known trade, measured (WCAG relative luminance, alpha composited onto
                  the sidebar surface #fafafa light / #18181b dark):

                                                   light     dark
                    nav rows / CONNECTED (dim)     7.41:1    6.91:1
                    NOT CONNECTED (text.disabled)  2.66:1    2.29:1   <- under AA 4.5

                  WCAG 1.4.3 exempts text in INACTIVE controls, but this row stays
                  clickable (it opens the reader popover), so the exemption does not
                  strictly apply. The label is redundant with the glyph and the popover
                  carries the full explanation, which is why this was judged acceptable.
                */
                color: isConnected ? dimNeutralText(theme) : theme.palette.text.disabled,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {statusLabel}
            </ListItemText>
          )}
        </ListItemButton>
      </ListItem>

      {/* Same components as the profile menu (SidebarUserBlock) for parity: a MUI Menu
          with MenuItem rows (leading icon + body2 label), same paper + list styling. A
          compact header gives context; the actions are rows, not buttons. */}
      <Menu
        action={menuPosition}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        // Right of the drawer, bottom-aligned with the row — the shared placement every
        // sidebar menu uses. This one previously opened ABOVE the row, centred on the
        // drawer, which put half the menu on top of the sidebar it belongs to.
        anchorOrigin={SIDEBAR_MENU_ANCHOR_ORIGIN}
        transformOrigin={SIDEBAR_MENU_TRANSFORM_ORIGIN}
        MenuListProps={{ sx: { pt: 2, pb: 0, minWidth: 240 } }}
        slotProps={{
          paper: {
            sx: {
              pl: 2,
              pr: 2,
              pb: 2,
              borderRadius: 2,
              // The house popover lift, shared from `@repo/ui` so the sidebar's three
              // popovers (Help, profile, this) cannot drift apart again.
              //
              // This said it "matches every other popover (theme MuiMenu paper,
              // shadows[3])" and did not: the Help popover — the actual reference for
              // what a menu looks like here — carries the blue-grey
              // `MENU_PAPER_SHADOW`, not MUI's neutral grey. The comment was true of
              // the theme default and false of the thing next to it on screen, which
              // is how this stayed wrong while looking deliberate.
              boxShadow: MENU_PAPER_SHADOW,
              ...SIDEBAR_MENU_PAPER_SX,
            },
          },
        }}
      >
        {/* Local-network permission heads-up, attached to the reader control rather
            than floating as the top-center toast it used to be. Shown only while the
            helper is UNREACHABLE — once it connects the permission was plainly
            granted, and the context retires the hint. That's why this no longer
            renders next to a "Reader connected" line, which it previously did. */}
        {/* Shown only when the helper is unreachable AND it has been reached from
            this browser before. That second condition matters: "unreachable" has
            three indistinguishable causes — not installed, not running, blocked by
            Chrome — and the browser exposes neither the helper's presence nor the
            Local Network Access permission (it isn't in navigator.permissions).
            A prior connection is the only hard evidence we have, and it rules out
            "not installed", so the permission/not-running framing is honest here.
            Someone who has never connected sees the Setup guide copy below
            instead, because for them "you haven't installed it" is the likely
            answer and a permission prompt would send them to fix the wrong thing. */}
        {permissionHintPending && !helperReachable && helperEverSeen && (
          <>
            <Box sx={{ px: 3, pt: 2, pb: 3, maxWidth: 260 }}>
              {/* Shared @repo/ui Alert, not a hand-rolled icon + Typography row —
                  a warning should look like every other warning in the app (§28),
                  and the hand-rolled version had to re-derive its own icon colour
                  and spacing. */}
              <Alert
                severity="warning"
                title="Can't reach your reader"
                description="The Vera Reader Helper isn't responding. Make sure it's running, and allow Chrome to connect to it if you're asked."
                sx={{ mb: 3 }}
              />
              {/* Retrying the 127.0.0.1 connection is what raises Chrome's prompt —
                  there is no API to request that permission directly. If the helper
                  simply isn't running, this reconnects once it is. */}
              <Button variant="secondary" onClick={requestLocalNetworkAccess} sx={{ width: '100%' }}>
                Try again
              </Button>
            </Box>
            <Divider flexItem sx={{ mb: 2 }} />
          </>
        )}
        <Box sx={{ px: 3, pt: 1, pb: 2, maxWidth: 260 }}>
          {isConnected ? (
            <>
              <Typography variant="body2">Reader connected</Typography>
              {readerName && (
                <Typography variant="body3" color="text.secondary">{readerName}</Typography>
              )}
            </>
          ) : helperReachable ? (
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Plug in a Vera Reader to start high-speed tag scanning and certificate
              creation. No reader? You can create certificates with your phone.
            </Typography>
          ) : (
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Connect a hardware reader for certificate creation and filtering. No
              reader? You can create certificates with your phone.
            </Typography>
          )}

          {/* The already-installed-but-blocked case.
              `helperEverSeen` is false for anyone who has never had a successful
              connection — which includes someone who HAS installed the helper but
              whose first attempt Chrome blocked. Without this they'd get only the
              copy above and be told to buy a reader they already own, when the real
              fix is a browser permission. We cannot tell the two apart (Local
              Network Access isn't exposed to navigator.permissions, and a blocked
              socket looks like a refused one), so we name both and let them pick. */}
          {!helperReachable && !helperEverSeen && (
            <Stack spacing={2} sx={{ mt: 3 }}>
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Already installed the Vera Reader Helper? Chrome may be blocking it.
              </Typography>
              <Button variant="secondary" onClick={requestLocalNetworkAccess}>
                Allow Chrome to connect
              </Button>
            </Stack>
          )}
        </Box>
        <Divider flexItem sx={{ mb: 2 }} />
        {/* Rows match the nearby Theme submenu item markup exactly (p:3, icon size 20,
            body2 label marginLeft 4, flexGrow) so the item size matches it 1:1. */}
        <MenuItem onClick={goSetup} sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction="row" spacing={3} alignItems="center" sx={{ width: '100%' }}>
            <BookOpenIcon strokeWidth={1} size={20} />
            <Typography variant="body2" sx={{ marginLeft: 4, flexGrow: 1 }}>
              {isConnected || helperReachable ? 'Reader setup guide' : 'Learn more'}
            </Typography>
          </Stack>
        </MenuItem>

        {/* "You can also scan with your phone using the Vera companion app" was a
            sentence describing an action you then had to go and find. It's a row
            now. Only offered when there's no reader to use — with one connected,
            the hardware path is the faster one and this would be noise. */}
        {!isConnected && (
          <MenuItem onClick={goCreateWithPhone} sx={{ p: 3, borderRadius: 2 }}>
            <Stack direction="row" spacing={3} alignItems="center" sx={{ width: '100%' }}>
              <SmartphoneIcon strokeWidth={1} size={20} />
              <Typography variant="body2" sx={{ marginLeft: 4, flexGrow: 1 }}>
                Create with phone
              </Typography>
            </Stack>
          </MenuItem>
        )}

        {!isConnected && !helperReachable && (
          <MenuItem onClick={goShop} sx={{ p: 3, borderRadius: 2 }}>
            <Stack direction="row" spacing={3} alignItems="center" sx={{ width: '100%' }}>
              <ShoppingBagIcon strokeWidth={1} size={20} />
              <Typography variant="body2" sx={{ marginLeft: 4, flexGrow: 1 }}>Shop readers</Typography>
            </Stack>
          </MenuItem>
        )}
      </Menu>
    </>
  )
}
